import { T, useGT } from 'gt-react';
import {
  PlusIcon,
  TrashIcon,
  DotsSixVerticalIcon,
} from '@phosphor-icons/react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import type { SortDraft } from './useQueryBuilderState';

/**
 * A sortable target: a field key, or an output-column alias (an aggregate
 * measure or date bucket) so summary results can be ordered by their measures.
 */
export interface SortOption {
  value: string;
  label: string;
}

interface SortPanelProps {
  /** Sortable targets for the current mode (fields and/or output aliases). */
  options: SortOption[];
  sorts: SortDraft[];
  /** Off when no more entries fit (defaults to true). */
  canAdd?: boolean;
  onAdd: () => void;
  onRemove: (id: string) => void;
  onChange: (id: string, patch: Partial<Omit<SortDraft, 'id'>>) => void;
  onReorder: (fromIndex: number, toIndex: number) => void;
}

/**
 * An ordered list of sort entries (field + direction). Each subsequent entry is
 * a tie-breaker for the ones before it; rows can be reordered via drag-and-drop
 * to change tie-break precedence.
 */
export function SortPanel({
  options,
  sorts,
  canAdd = true,
  onAdd,
  onRemove,
  onChange,
  onReorder,
}: SortPanelProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const fromIndex = sorts.findIndex((s) => s.id === active.id);
    const toIndex = sorts.findIndex((s) => s.id === over.id);
    if (fromIndex === -1 || toIndex === -1) return;
    onReorder(fromIndex, toIndex);
  }

  return (
    <div className="space-y-2">
      {sorts.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          <T context="empty state for the sort section">
            No sort applied. Add one to order the results.
          </T>
        </p>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={sorts.map((s) => s.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2">
              {sorts.map((sort, index) => (
                <SortRow
                  key={sort.id}
                  sort={sort}
                  index={index}
                  options={options}
                  onRemove={onRemove}
                  onChange={onChange}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <Button
        variant="outline"
        size="sm"
        onClick={onAdd}
        disabled={!canAdd}
        className="gap-1.5"
      >
        <PlusIcon className="h-4 w-4" />
        <T context="button label to add a sort entry">Add sort</T>
      </Button>
    </div>
  );
}

interface SortRowProps {
  sort: SortDraft;
  index: number;
  options: SortOption[];
  onRemove: (id: string) => void;
  onChange: (id: string, patch: Partial<Omit<SortDraft, 'id'>>) => void;
}

function SortRow({ sort, index, options, onRemove, onChange }: SortRowProps) {
  const gt = useGT();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sort.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-2">
      <button
        type="button"
        className="flex h-8 w-5 shrink-0 cursor-grab items-center justify-center text-muted-foreground hover:text-foreground active:cursor-grabbing"
        aria-label={gt('Drag to reorder sort', {
          context: 'aria label for the drag handle reordering a sort entry',
        })}
        {...attributes}
        {...listeners}
      >
        <DotsSixVerticalIcon className="h-4 w-4" />
      </button>
      <span className="w-5 text-right text-xs text-muted-foreground">
        {index + 1}
      </span>
      <Select
        value={sort.field || '__none__'}
        onValueChange={(next) =>
          onChange(sort.id, { field: next === '__none__' ? '' : next })
        }
      >
        <SelectTrigger className="h-8 w-56 text-sm">
          <SelectValue
            placeholder={gt('Field', {
              context: 'placeholder for sort field selector',
            })}
          />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">
            <T context="sort option for no field selected">None</T>
          </SelectItem>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <ToggleGroup
        type="single"
        value={sort.direction}
        onValueChange={(next) => {
          if (next === 'asc' || next === 'desc')
            onChange(sort.id, { direction: next });
        }}
        size="sm"
      >
        <ToggleGroupItem value="asc" className="h-8 px-3 text-xs">
          <T context="ascending sort direction toggle">Asc</T>
        </ToggleGroupItem>
        <ToggleGroupItem value="desc" className="h-8 px-3 text-xs">
          <T context="descending sort direction toggle">Desc</T>
        </ToggleGroupItem>
      </ToggleGroup>
      <Button
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0"
        onClick={() => onRemove(sort.id)}
        aria-label={gt('Remove sort', {
          context: 'aria label for removing a sort entry',
        })}
      >
        <TrashIcon className="h-4 w-4" />
      </Button>
    </div>
  );
}
